import { calculateDifferenceInDays } from '../../helpers/date';
import { convertScaledValueToDecimal } from '../../helpers/number';
import { OrderOverviewOrder } from '../@account/OrderOverview/OrderOverview';
import {
    defaultGTMDaysBetweenTodayAndLastPurchase,
    GTMCompletedOrder,
    GTMProduct,
    GTMProductItem,
    GTMProductList,
} from '../@api/Sylius/Resource/GTM';
import { Product } from '../@products/Product/Product';
import { CartItem } from '../@shop/Cart/Cart';
import { OrderInterface } from '../@shop/Order/Order';
import { Currency } from '../Price/Price';

export const generateGTMProductInfo = (product: Product, index: number = 0): GTMProductItem => ({
    index,
    item_id: product.legacyId || product.variantCode,
    item_name: product.name,
    item_brand: product.brand?.label || '',
    discount: product.price.discountInPrice,
    price: product.price.current,
    quantity: 1,
    item_category: product.categories && product.categories[0],
    item_category2: product.categories && product.categories[1],
    item_category3: product.categories && product.categories[2],
    item_category4: product.categories && product.categories[3],
    item_category5: product.categories && product.categories[4],
});

export const generateCartItemToGTMProduct = (cartItem: CartItem, index: number = 0): GTMProductItem => {
    const price = cartItem.price.current / cartItem.quantity;

    return {
        item_id: cartItem.legacyId || cartItem.variantCode,
        item_name: cartItem.name,
        item_brand: cartItem.brand?.label || '',
        price,
        discount: cartItem.price.discountInPrice,
        quantity: cartItem.quantity,
        item_category: cartItem.categories && cartItem.categories[0],
        item_category2: cartItem.categories && cartItem.categories[1],
        item_category3: cartItem.categories && cartItem.categories[2],
        item_category4: cartItem.categories && cartItem.categories[3],
        item_category5: cartItem.categories && cartItem.categories[4],
        index,
    };
};

export const generateGTMProduct = (product: Product): GTMProduct => ({
    currency: Currency.eur,
    value: product.price.current,
    items: [generateGTMProductInfo(product)],
});

export const generateGTMCart = (cart: OrderInterface): GTMProduct => {
    const items = cart.items.map((item, index) => generateCartItemToGTMProduct(item, index));

    return {
        currency: Currency.eur,
        value: convertScaledValueToDecimal(cart.totalPrice),
        items,
    };
};

export const generateGTMProductList = (products: Product[]): GTMProductList => {
    const items = products.map(generateGTMProductInfo);

    return {
        currency: Currency.eur,
        items,
    };
};

export const generateGTMCompletedOrder = (order: OrderInterface, isNewCustomer?: boolean): GTMCompletedOrder => {
    const items = order.items.map((item, index) => generateCartItemToGTMProduct(item, index));

    const newCustomer = isNewCustomer
        ? { new_customer: isNewCustomer }
        : undefined;

    return {
        ...newCustomer,
        currency: Currency.eur,
        value: convertScaledValueToDecimal(order.totalPrice),
        transaction_id: order.loods5Id || '',
        shipping: 0,
        items,
    };
};

// Source: https://support.google.com/google-ads/answer/12077475#zippy=%2Cinstall-with-google-tag-manager
export const checkIfExistingCustomerIsNew = (order: OrderOverviewOrder): boolean => {
    const { orderDate } = order;

    const daysBetweenLastOrderAndToday = orderDate
        ? calculateDifferenceInDays(orderDate)
        : 0;

    return daysBetweenLastOrderAndToday >= defaultGTMDaysBetweenTodayAndLastPurchase;
};
